<template>
  <v-card tile>
    <v-app-bar dark flat color="primary">
      <v-btn icon dark @click="cancel(company)">
        <v-icon>close</v-icon>
      </v-btn>
      <span class="title">{{
        isNew ? resx.labels.newCompany : resx.labels.editCompany
      }}</span>
    </v-app-bar>
    <form @submit.prevent="save()" novalidate v-if="company">
      <v-card-text>
        <v-container grid-list-xl>
          <v-row wrap>
            <v-col cols="12" md="6">
              <v-row>
                <v-col>
                  <v-text-field
                    :label="resx.labels.companyName"
                    v-model="company.name"
                    name="companyName"
                    :data-vv-as="resx.labels.companyName"
                    v-validate.initial="`required|uniqueName:${company._id}`"
                    :error-messages="errors.collect('companyName')"
                    required
                  >
              </v-text-field>
            </v-col>
          </v-row>
        </v-col>

            <v-col cols="12" md="6">
              <v-row row wrap>
                <v-col d-flex xs12 sm12 md4>
                  <v-autocomplete
                    data-vv-name="identifierType"
                    :data-vv-as="resx.labels.identifierType"
                    :error-messages="errors.collect('identifierType')"
                    v-validate.initial="'required'"
                    :items="identifierTypes"
                    v-model="company.identifier.type"
                    :label="resx.labels.identifierType"
                    item-text="text"
                    item-value="id"
                    cache-items
                    required
                    :no-data-text="resx.messages.noSelectorDataAvailable"
                    :disabled="identifierTypes.length === 0"
                  ></v-autocomplete>
                </v-col>

                <v-col d-flex xs12 sm12 md8>
                  <v-text-field
                    :label="resx.labels.identifier"
                    v-model="company.identifier.number"
                    name="identifierNumber"
                    :data-vv-as="resx.labels.identifier"
                    :disabled="company.identifier.type == undefined"
                    type="text"
                    return-masked-value
                    :mask="identifierMask"
                    v-validate.initial="{
                      required: true,
                      uniqueid: [company._id],
                      identifierValid: [company.identifier.type]
                    }"
                    :error-messages="errors.collect('identifierNumber')"
                    required
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.address"
                :label="resx.labels.addressStreet"
                name="address"
                :data-vv-as="resx.labels.addressStreet"
                v-validate.initial="`required`"
                :error-messages="errors.collect('address')"
                required
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.addressNumber"
                :label="resx.labels.addressNumber"
                name="addressNumber"
                :data-vv-as="resx.labels.addressNumber"
                v-validate.initial="'required|numeric|max_value:99999'"
                :error-messages="errors.collect('addressNumber')"
                required
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.floor"
                :label="resx.labels.floor"
                name="floor"
                :data-vv-as="resx.labels.floor"
                :error-messages="errors.collect('floor')"
                required
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.office"
                :label="resx.labels.office"
                name="office"
                :data-vv-as="resx.labels.office"
                :error-messages="errors.collect('office')"
                required
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.zipcode"
                :label="resx.labels.zipcode"
                name="zipcode"
                :data-vv-as="resx.labels.zipcode"
                v-validate.initial="'required|numeric|max_value:9999'"
                :error-messages="errors.collect('zipcode')"
                required
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.locality"
                :label="resx.labels.locality"
                name="locality"
                :data-vv-as="resx.labels.locality"
                v-validate.initial="'required'"
                :error-messages="errors.collect('locality')"
                required
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-autocomplete
                data-vv-name="province"
                :data-vv-as="resx.labels.province"
                :error-messages="errors.collect('province')"
                v-validate.initial="'required'"
                :items="provinces"
                v-model="company.province"
                :label="resx.labels.province"
                item-text="name"
                item-value="name"
                cache-items
                required
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="6">
              <v-switch
                color="primary"
                :label="
                  `${resx.labels.company} ${
                    company.isActive ? resx.labels.active : resx.labels.inactive
                  }`
                "
                v-model="company.isActive"
                :disabled="company.isBuiltIn"
                required
              >
              </v-switch>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.phone"
                :label="resx.labels.phone"
                name="phone"
                :data-vv-as="resx.labels.phone"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.email"
                :label="resx.labels.email"
                name="email"
                v-validate.initial="'email'"
                :data-vv-as="resx.labels.email"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.contactName"
                :label="resx.labels.contactName1"
                name="contactName"
                :data-vv-as="resx.labels.contactName"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.contactName1"
                :label="resx.labels.contactName2"
                name="contactName1"
                :data-vv-as="resx.labels.contactName1"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.contactPhone"
                :label="resx.labels.contactPhone1"
                name="contactPhone"
                :data-vv-as="resx.labels.contactPhone"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.contactPhone1"
                :label="resx.labels.contactPhone2"
                name="contactPhone1"
                :data-vv-as="resx.labels.contactPhone1"
              />
            </v-col>

            <!-- Mail de contacto 1  -->

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.contactEmail"
                :label="resx.labels.contactEmail1"
                name="contactEmail"
                :data-vv-as="resx.labels.contactEmail"
                v-validate.initial="'email'"
                :error-messages="errors.collect('contactEmail')"
              />
            </v-col>

            <!-- Mail de contacto 2  -->

            <v-col cols="12" md="6">
              <v-text-field
                v-model="company.contactEmail1"
                :label="resx.labels.contactEmail2"
                name="contactEmail1"
                :data-vv-as="resx.labels.contactEmail1"
                v-validate.initial="'email'"
                :error-messages="errors.collect('contactEmail1')"
              />
            </v-col>






            <v-col cols="12" md="6">
                <v-switch
                  color="primary"
                  :label="
                    `${resx.labels.sendEmailToCompanyActivation} ${
                      company.sendEmailToCompany
                        ? resx.labels.active
                        : resx.labels.inactive
                    }`
                  "
                  v-model="company.sendEmailToCompany"
                />
            </v-col>
            <v-col cols="12" md="6">
                <v-switch
                  color="primary"
                  :label="
                    `${resx.labels.sendEmailToContractorsActivation} ${
                      company.sendEmailToContractors
                        ? resx.labels.active
                        : resx.labels.inactive
                    }`
                  "
                  v-model="company.sendEmailToContractors"
                />
            </v-col>
            <v-col cols="12" md="6">
                <v-switch
                  color="primary"
                  :label="
                    `${resx.labels.sendDailyEmailActivation} ${
                      company.sendDailyEmail
                        ? resx.labels.active
                        : resx.labels.inactive
                    }`
                  "
                  v-model="company.sendDailyEmail"
                />
            </v-col>
            <v-col cols="12" md="6">
                <v-switch
                  color="primary"
                  :label="
                    `${resx.labels.sendMonthlyEmailActivation} ${
                      company.sendMonthlyEmail
                        ? resx.labels.active
                        : resx.labels.inactive
                    }`
                  "
                  v-model="company.sendMonthlyEmail"
                />
                <schedule-selector
                  v-if="company.sendMonthlyEmail && !company.manualMode"
                  v-model="company.monthlySchedule"
                  day-label="Día del envío mensual"
                />
            </v-col>


            <v-col cols="12">
              <v-divider class="my-3" />
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="company.emailSenderAddress"
                    :label="resx.labels.emailSenderAddress"
                    name="emailSenderAddress"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="company.emailBccAddress"
                    :label="resx.labels.emailBccAddress"
                    name="emailBccAddress"
                    :data-vv-as="resx.labels.emailBccAddress"
                    v-validate.initial="'email'"
                    :error-messages="errors.collect('emailBccAddress')"
                  />
                </v-col>
                <v-col cols="12">
                  <div class="body-2 mb-1">{{ resx.labels.emailFooter }}</div>
                  <vue-editor
                    v-model="company.emailFooter"
                    :editor-toolbar="editorToolbar"
                  />
                </v-col>
                <v-col cols="12">
                  <div class="body-2 mb-1">{{ resx.labels.emailSignature }}</div>
                  <vue-editor
                    v-model="company.emailSignature"
                    :editor-toolbar="editorToolbar"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="company.emailStyle"
                    :items="emailStyles"
                    :label="resx.labels.emailStyle"
                    item-text="text"
                    item-value="value"
                    class="cursor-pointer"
                  />
                </v-col>
                <!-- El encabezado y el cierre solo los usa el estilo 2: fuera
                     de el se editarian a ciegas, sin ver nunca el resultado. -->
                <template v-if="company.emailStyle === 2">
                  <v-col cols="12">
                    <v-alert dense text type="info" class="body-2 mb-0">
                      Marcadores disponibles para el encabezado y el cierre:
                      <span v-for="token in emailTokens" :key="token.token" class="d-inline-block mr-4">
                        <code>{{ token.token }}</code> {{ token.description }}
                      </span>
                    </v-alert>
                  </v-col>
                  <v-col cols="12">
                    <div class="body-2 mb-1">{{ resx.labels.emailOpening }}</div>
                    <vue-editor
                      v-model="company.emailOpening"
                      :editor-toolbar="editorToolbar"
                    />
                  </v-col>
                  <v-col cols="12">
                    <div class="body-2 mb-1">{{ resx.labels.emailClosing }}</div>
                    <vue-editor
                      v-model="company.emailClosing"
                      :editor-toolbar="editorToolbar"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox
                      v-model="showObligationDueDates"
                      :label="resx.labels.showObligationDueDates"
                      class="mt-0"
                      hide-details
                    />
                  </v-col>
                </template>
              </v-row>
            </v-col>

            <v-col cols="12">
              <v-divider class="my-3" />
              <div class="subtitle-1 mb-3">Envíos por etiquetas</div>
              <v-data-table
                :headers="tagMailingHeaders"
                :items="activeTagMailings"
                hide-default-footer
                no-data-text="No hay envíos configurados"
              >
                <template v-slot:item.schedule="{ item }">
                  {{ describeSchedule(item.schedule) }}
                </template>
                <template v-slot:item.isActive="{ item }">
                  {{ item.isActive ? resx.labels.active : resx.labels.inactive }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn icon @click="editingTagMailing = item">
                    <v-icon color="primary">mode_edit</v-icon>
                  </v-btn>
                  <v-btn icon @click="removeTagMailing(item)">
                    <v-icon color="error">delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
              <v-btn class="mt-2" @click="editingTagMailing = {}">Agregar envío</v-btn>
            </v-col>

            <v-col cols="12" v-if="!company.isBuiltIn">
              <v-divider class="my-3" />
              <div class="subtitle-1 mb-3">{{ resx.labels.systemUsersAccess }}</div>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="body-2 mb-2">{{ resx.labels.allowedRoles }}</div>
                  <v-checkbox
                    v-for="role in systemRoles"
                    :key="role.code"
                    :label="role.name"
                    :value="role.code"
                    v-model="company.allowedRoles"
                    hide-details
                    class="mt-1"
                  />
                  <div v-if="systemRoles.length === 0" class="body-2 grey--text">
                    {{ resx.messages.noSelectorDataAvailable }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="body-2 mb-2">{{ resx.labels.allowedUsers }}</div>
                  <v-checkbox
                    v-for="user in systemUsers"
                    :key="user._id"
                    :label="`${user.firstname} ${user.lastname} (${user.username})`"
                    :value="user._id"
                    v-model="company.allowedUsers"
                    hide-details
                    class="mt-1"
                  />
                  <div v-if="systemUsers.length === 0" class="body-2 grey--text">
                    {{ resx.messages.noSelectorDataAvailable }}
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12">
              <comments-component
                v-model="company.comments"
                mode="edit"
                :label="resx.labels.comments"
                @input="company.comments = $event"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="cancel(company)">{{ resx.labels.cancel }}</v-btn>
        <v-btn class="primary" type="submit" :disabled="!isValidForm">{{
          resx.labels.ok
        }}</v-btn>
      </v-card-actions>
    </form>

    <tag-mailing-dialog
      v-if="editingTagMailing"
      :tag-mailing="editingTagMailing._id ? editingTagMailing : null"
      @save="onTagMailingSave"
      @close="editingTagMailing = null"
    >
    </tag-mailing-dialog>
  </v-card>
</template>

<script>
"use strict";

import store from "@/store/store";
import validations from "@/utils/validations";
import { sprintf } from "sprintf-js";
import companyService from "@/services/CompanyService";
import userService from "@/services/UserService";
import commentsComponent from "@/components/common/comments.vue";
import { VueEditor } from "vue2-editor";
import scheduleSelector from "@/components/views/companies/schedule-selector.component.vue";
import tagMailingDialog from "@/components/views/companies/tag-mailing-dialog.component.vue";

const uniqueid = {
  getMessage() {
    return store.state.locale.resx.messages.uniqueIdentifier;
  },
  async validate(value, args) {
    const isValid = await companyService.isValidIdentifier(args[0], value);
    return {
      valid: isValid ? isValid.result : false
    };
  }
};
const uniqueName = {
  getMessage() {
    return store.state.locale.resx.messages.uniqueName;
  },
  async validate(value, args) {
    const isValid = await companyService.isValidName(args[0], value);
    return {
      valid: isValid ? isValid.result : false
    };
  }
};
const identifierValid = {
  getMessage() {
    return store.state.locale.resx.messages.identifierInvalid;
  },
  validate(value, args) {
    return validations.isValidIdentifer(value, args);
  }
};
const isValidEmail = {
  getMessage() {
    return store.state.locale.resx.messages.uniqueEmail;
  },
  async validate(value, args) {
    const isValid = await companyService.isValidEmail(args[0], value);
    return {
      valid: isValid ? isValid.result : false
    };
  }
};

export default {
  name: "edit-company",
  created() {
    // Va en created() y no en mounted(): el created() del hijo schedule-selector
    // corre ANTES que el mounted() del padre, y ese hijo normaliza y emite si
    // recibe un valor vacio. Si esta guarda corriera en mounted(), el default
    // del hijo (dia 1, su primera opcion permitida) ya habria ganado y esta
    // asignacion nunca se ejecutaria: toda empresa existente con mensual activo
    // quedaria en dia 1 al abrirla, pisando el default de primer dia habil.
    if (!this.company.monthlySchedule) {
      this.$set(this.company, "monthlySchedule", { dayType: "firstBusinessDay", dayOfMonth: null });
    }
    // Una empresa creada antes de los envios por etiquetas no trae el array.
    if (!Array.isArray(this.company.tagMailings)) {
      this.$set(this.company, "tagMailings", []);
    }
  },
  async mounted() {
    this.$validator.extend("uniqueid", uniqueid);
    this.$validator.extend("uniqueName", uniqueName);
    this.$validator.extend("identifierValid", identifierValid);
    this.$validator.extend("isValidEmail", isValidEmail);
    // const identifiers = await contractorService.lookupIdentifers();
    this.identifierTypes = [
      {
        id: store.state.constants.IDENTIFIERS.CUIT,
        text: store.state.constants.IDENTIFIERS.CUIT.toUpperCase()
      },
      {
        id: store.state.constants.IDENTIFIERS.CUIL,
        text: store.state.constants.IDENTIFIERS.CUIL.toUpperCase()
      },
      {
        id: store.state.constants.IDENTIFIERS.DNI,
        text: store.state.constants.IDENTIFIERS.DNI.toUpperCase()
      }
    ];
    this.provinces = store.state.constants.PROVINCES;

    this.companyChange(this.company);

    if (!this.company.isBuiltIn) {
      const allRoles = await userService.getRoles();
      this.systemRoles = allRoles.filter(
        r => r.isBuiltIn && r.code !== store.state.constants.roles.SYS_ADMIN
      );
      this.systemUsers = await userService.getSystemUsers();
    }
  },
  components: {
    commentsComponent,
    VueEditor,
    "schedule-selector": scheduleSelector,
    "tag-mailing-dialog": tagMailingDialog
  },
  computed: {
    isNew: function() {
      return !(this.company && this.company._id);
    },
    // Los envios borrados siguen en el array -ver removeTagMailing()- pero no
    // se muestran.
    activeTagMailings: function() {
      return (this.company.tagMailings || []).filter(m => !m.isDeleted);
    },
    // Una empresa vieja no tiene el campo, y el renderer trata la ausencia como
    // prendido: el checkbox tiene que leerse con la misma regla o mentiria.
    showObligationDueDates: {
      get: function() {
        return this.company.showObligationDueDates !== false;
      },
      set: function(value) {
        this.$set(this.company, "showObligationDueDates", value);
      }
    }
  },
  data() {
    return {
      identifierTypes: [],
      identifierMask: "",
      originalCompany: {},
      provinces: [],
      systemRoles: [],
      systemUsers: [],
      editorToolbar: [
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline"],
        [{ color: [] }],
        ["link"],
        [{ align: [] }],
        ["clean"]
      ],
      emailStyles: [
        { text: "Estilo original Contratados", value: 1 },
        { text: "Estilo simple", value: 2 }
      ],
      emailTokens: [
        { token: "$NOMBRE", description: "nombre del contacto" },
        { token: "$APELLIDO", description: "apellido del contacto" },
        { token: "$CONTACTO", description: "contacto completo" },
        { token: "$CONTRATISTA", description: "razón social" },
        { token: "$CORREO", description: "correo remitente" },
        { token: "$FECHA", description: "fecha límite" }
      ],
      editingTagMailing: null,
      tagMailingHeaders: [
        { text: "Nombre", value: "name" },
        { text: "Día", value: "schedule", sortable: false },
        { text: "Activo", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false, width: 120 }
      ]
    };
  },
  methods: {
    describeSchedule(schedule) {
      if (!schedule) return "—";
      const labels = {
        lastDay: "Último día del mes",
        lastBusinessDay: "Último día hábil del mes",
        firstBusinessDay: "Primer día hábil del mes"
      };
      return labels[schedule.dayType] || `Día ${schedule.dayOfMonth}`;
    },
    onTagMailingSave(form) {
      const list = this.company.tagMailings || [];
      const index = this.editingTagMailing._id
        ? list.findIndex(m => m._id === this.editingTagMailing._id)
        : -1;
      if (index === -1) {
        list.push(form);
      } else {
        this.$set(list, index, Object.assign({}, list[index], form));
      }
      this.$set(this.company, "tagMailings", list);
      this.editingTagMailing = null;
    },
    // Borrado logico: si se sacara del array, Mongo perderia el _id y la guarda
    // de idempotencia de los envios anteriores dejaria de casar.
    removeTagMailing(item) {
      const index = this.company.tagMailings.indexOf(item);
      if (index === -1) return;
      this.$set(
        this.company.tagMailings,
        index,
        Object.assign({}, item, { isDeleted: true, isActive: false })
      );
    },
    async cancel(company) {
      if (JSON.stringify(this.originalCompany) != JSON.stringify(this.company)) {
        await this.botConfirmDialog({
          title: `${
          this.resx.labels.edit
          } ${this.resx.labels.company.toLowerCase()}`,
          content: sprintf(
          this.resx.messages.noSavedChanges,
          company.name
          ),
          alertHint: ""
        });
        this.$emit("cancel");
        this.originalCompany = {};
      } else {
        this.$emit("cancel");
        this.originalCompany = {};
      }
    },
    async save() {
      let result = null;
      if (this.isNew) {
        result = await companyService.addCompany(this.company);
      } else {
        result = await companyService.updateCompany(this.company);
      }
      if (result) {
        this.$emit("saved", { isNew: this.isNew, company: result });
      }
      this.originalCompany = {};
    },
    assignMask(value) {
      switch (value) {
        case store.state.constants.IDENTIFIERS.CUIT:
          this.identifierMask = "##-########-#";
          break;
        case store.state.constants.IDENTIFIERS.CUIL:
          this.identifierMask = "##-########-#";
          break;
        case store.state.constants.IDENTIFIERS.DNI:
          this.identifierMask = "########";
          break;
        default:
          this.identifierMask = "";
          break;
      }
    },
    companyChange(company) {
      if (typeof this.originalCompany._id === "undefined") {
        this.originalCompany = Object.assign({}, company);
      };
    }
  },
  props: {
    company: { type: Object }
  },
  watch: {
    "company.identifier.type"(val) {
      if (val) {
        this.assignMask(val);
      }
    },

    "selectedItem.contractor"(val) {
      if (val && !this.selectedItem._id) {
        const contractor = this.contractors.find(c => c._id == val);
        this.selectedItem.contactEmail = contractor.contactEmail;
        this.selectedItem.contactPhone = contractor.contactPhone;
        this.selectedItem.contactEmail1 = contractor.contactEmail1;
        this.selectedItem.contactPhone1 = contractor.contactPhone1;
      }
    }
  }
};
</script>

<style lang="scss">
.ql-snow .ql-picker.ql-size .ql-picker-label:not([data-value])::before,
.ql-snow .ql-picker.ql-size .ql-picker-item:not([data-value])::before { content: 'Normal'; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="small"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="small"]::before { content: 'Pequeño'; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="large"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="large"]::before { content: 'Grande'; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="huge"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="huge"]::before { content: 'Muy grande'; }
</style>
